// ============================================================================
// HUQUQLAR KATALOGI
//
// Tizimdagi barcha huquqlar shu yerda, bitta joyda. Server (RPC'lardagi
// require_perm), ega paneli (rollar sahifasi) va kassa ilovasi (tugmalarni
// ko'rsatish/yashirish) aynan shu ro'yxatdan foydalanadi.
//
// Qoidalar:
//  - Kalit (key) bir marta beriladi va HECH QACHON o'zgartirilmaydi yoki
//    qayta ishlatilmaydi — bazada saqlangan sozlamalar shu kalitga bog'liq.
//  - Yangi huquq qo'shilsa, rol shablonlariga standart qiymati bilan qo'shiladi.
// ============================================================================

import type { ModuleKey } from './modules';

export const PERMISSION_GROUPS = {
  savdo: 'Savdo',
  buyurtma: 'Buyurtma',
  oshxona: 'Oshxona',
  kassa: 'Kassa',
  sklad: 'Sklad',
  menyu: 'Menyu',
  hisobot: 'Hisobot',
  boshqaruv: 'Boshqaruv',
} as const;

export type PermissionGroup = keyof typeof PERMISSION_GROUPS;

export type LimitKind = 'percent' | 'amount';

export interface PermissionDef {
  key: string;
  group: PermissionGroup;
  label: string;
  description: string;
  /** Huquq faqat shu modul yoqilgan bo'lsa ma'noga ega */
  module?: ModuleKey;
  /** Huquqqa limit qo'yish mumkin (chegirma foizi, qaytarish summasi) */
  limit?: { kind: LimitKind; label: string };
  /** Xavfli amal: offline'da joyida ruxsati bor odam PIN'i so'raladi va egaga ko'rsatiladi */
  sensitive?: boolean;
  /**
   * Bu huquq faqat shu huquqlar bilan birga ishlaydi (masalan, chegirma — savdosiz ma'nosiz).
   * Asosiy huquq o'chirilsa, bu huquq ham amalda o'chadi (server va ilova bir xil hisoblaydi).
   * Faqat bir pog'ona: talab qilinadigan huquqning o'zida `requires` bo'lmaydi.
   */
  requires?: readonly string[];
  /** Imkoniyat hali tayyor emas: rollar sahifasida ko'rinmaydi va hech kimga berilmaydi */
  planned?: boolean;
}

export const PERMISSIONS = [
  // ---- Savdo ----
  { key: 'pos.sell', group: 'savdo', label: 'Savdo qilish', description: 'Buyurtma yaratish va savatga mahsulot qo‘shish' },
  { key: 'pos.take_payment', group: 'savdo', label: 'To‘lov qabul qilish', description: 'Buyurtma uchun naqd, karta yoki boshqa usulda to‘lov olish' },
  { key: 'pos.discount', group: 'savdo', label: 'Chegirma berish', description: 'Buyurtmaga chegirma qo‘yish', limit: { kind: 'percent', label: 'Eng ko‘p chegirma, %' }, sensitive: true, requires: ['pos.sell'] },
  { key: 'pos.custom_price', group: 'savdo', label: 'Erkin narx kiritish', description: 'Menyuda yo‘q mahsulotni qo‘lda kiritilgan narx bilan sotish', sensitive: true, requires: ['pos.sell'] },
  { key: 'pos.reprint', group: 'savdo', label: 'Chekni qayta chiqarish', description: 'Avvalgi buyurtma chekini yana chop etish' },
  { key: 'pos.hall', group: 'savdo', label: 'Zal va stollar', description: 'Stolga buyurtma ochish va ko‘rish', module: 'hall', requires: ['pos.sell'] },
  { key: 'pos.precheck', group: 'savdo', label: 'Hisob cheki chiqarish', description: 'To‘lovdan oldin mijozga hisob chekini (prechek) berish. Sozlamada yoqilgan bo‘lsa, shundan keyin hisob qulflanadi', module: 'hall' },
  { key: 'pos.other_tables', group: 'savdo', label: 'Boshqa ofitsiant stolini o‘zgartirish', description: 'Ofitsiant telefonida boshqa ofitsiant ochgan stolga taom qo‘shish, hisob cheki, ko‘chirish', module: 'waiters' },
  { key: 'pos.transfer_table', group: 'savdo', label: 'Stolni boshqa ofitsiantga o‘tkazish', description: 'Stol (buyurtma) egasini boshqa ofitsiantga almashtirish — jurnalga yoziladi', module: 'waiters' },
  { key: 'pos.bill_unlock', group: 'savdo', label: 'Hisob chekidan keyin hisobni ochish', description: 'Hisob cheki chiqqach qulflangan hisobga yana taom qo‘shish yoki o‘zgartirish uchun ochish (sabab bilan, jurnalga yoziladi). “Buyurtmani bekor qilish” huquqi borlar ham ocha oladi', module: 'hall', sensitive: true },
  { key: 'pos.my_sales', group: 'savdo', label: 'O‘z savdosini ko‘rish', description: 'Ofitsiant telefonida “Mening smenam”: bugungi stollari, savdosi, xizmat haqi, olgan to‘lovlari', module: 'waiters' },

  // ---- Buyurtma ----
  { key: 'order.void', group: 'buyurtma', label: 'Buyurtmani bekor qilish', description: 'Buyurtmani sabab bilan bekor qilish, sklad qaytariladi', sensitive: true },
  { key: 'order.refund', group: 'buyurtma', label: 'Pul qaytarish', description: 'To‘langan buyurtma uchun pulni qaytarish', limit: { kind: 'amount', label: 'Eng ko‘p summa, so‘m' }, sensitive: true },
  { key: 'order.history', group: 'buyurtma', label: 'Buyurtmalar tarixini ko‘rish', description: 'O‘tgan buyurtmalar ro‘yxati va tafsilotlari' },
  { key: 'order.status', group: 'buyurtma', label: 'Buyurtma holatini o‘zgartirish', description: 'Tayyorlanmoqda, tayyor, berildi' },

  // ---- Oshxona ----
  { key: 'kds.use', group: 'oshxona', label: 'Oshxona ekrani', description: 'Oshxona ekranida buyurtmalar navbati bilan ishlash', module: 'kds' },

  // ---- Kassa ----
  { key: 'shift.open_close', group: 'kassa', label: 'Smena ochish va yopish', description: 'Kassa smenasini boshlash va yakunlash' },
  { key: 'shift.see_expected', group: 'kassa', label: 'Yopishda kutilgan summani ko‘rish', description: 'O‘chirilsa kassir “ko‘r yopadi”: sanab kiritadi, farqni faqat menejer ko‘radi', requires: ['shift.open_close'] },
  { key: 'cash.move', group: 'kassa', label: 'Kassaga pul kiritish yoki olish', description: 'Inkassatsiya va kassaga pul qo‘yish', limit: { kind: 'amount', label: 'Eng ko‘p summa, so‘m' }, sensitive: true },
  { key: 'expense.add', group: 'kassa', label: 'Xarajat qo‘shish', description: 'Kassadan yoki tashqaridan xarajat yozish', module: 'expenses' },
  { key: 'meal.give', group: 'kassa', label: 'Xodim ovqatini berish', description: 'Xodimga belgilangan tekin ovqatni berish', module: 'staff_meals' },

  // ---- Sklad ----
  { key: 'stock.view', group: 'sklad', label: 'Skladni ko‘rish', description: 'Xom ashyo qoldiqlari va harakatlari', module: 'inventory' },
  { key: 'stock.receive', group: 'sklad', label: 'Kirim qilish', description: 'Yetkazib berilgan mahsulotni qabul qilish', module: 'inventory' },
  { key: 'stock.receive_price', group: 'sklad', label: 'Kirimda narx yozish', description: 'Kirimda qancha to‘langanini kiritish. Xom ashyoga narx chegarasi qo‘yilgan bo‘lsa — undan oshmaydi', module: 'inventory', requires: ['stock.receive'] },
  { key: 'stock.receive_over', group: 'sklad', label: 'Kirim narxini nazorat qilish', description: 'Chegaradan yuqori narx bilan kirim, kutilayotgan narxni tasdiqlash, kirimni “tekshirildi” deb belgilash, narx chegarasini o‘zgartirish', module: 'inventory', sensitive: true, requires: ['stock.receive'] },
  { key: 'stock.adjust', group: 'sklad', label: 'Inventarizatsiya va isrof', description: 'Sanash natijasi va isrofni yozish', module: 'inventory', sensitive: true, requires: ['stock.view'] },
  { key: 'stock.view_cost', group: 'sklad', label: 'Tannarx va xarid narxini ko‘rish', description: 'Xom ashyo narxi va mahsulot tannarxi', module: 'inventory', requires: ['stock.view'] },
  { key: 'stock.edit_materials', group: 'sklad', label: 'Xom ashyo va retseptni tahrirlash', description: 'Xom ashyo qo‘shish, retsept tuzish', module: 'inventory' },

  // ---- Menyu ----
  { key: 'menu.edit', group: 'menyu', label: 'Menyuni tahrirlash', description: 'Kategoriya, mahsulot, modifikator qo‘shish va o‘zgartirish' },
  { key: 'menu.price', group: 'menyu', label: 'Narxni o‘zgartirish', description: 'Mahsulot va modifikator narxlari', requires: ['menu.edit'] },
  { key: 'menu.channels', group: 'menyu', label: 'Savdo kanallari va narxlari', description: 'Agregator (Uzum, Yandex…) narxi, komissiya, kanalda sotiladigan mahsulotlar', module: 'aggregators', requires: ['menu.edit'], sensitive: true },
  { key: 'menu.availability', group: 'menyu', label: '“Tugadi” belgisini qo‘yish', description: 'Mahsulotni vaqtincha sotuvdan olish' },

  // ---- Hisobot ----
  { key: 'report.sales', group: 'hisobot', label: 'Sotuv hisobotlari', description: 'Kunlik va davr bo‘yicha savdo' },
  { key: 'report.profit', group: 'hisobot', label: 'Foyda va tannarx hisobotlari', description: 'Sof foyda, tannarx, mahsulot foydaliligi' },
  { key: 'report.staff', group: 'hisobot', label: 'Xodimlar nazorati', description: 'Xodimlar bo‘yicha savdo, bekor qilishlar, chegirmalar' },
  { key: 'report.journal', group: 'hisobot', label: 'Jurnal (kim nima qildi)', description: 'Barcha muhim amallar tarixi' },

  // ---- Boshqaruv ----
  { key: 'staff.manage', group: 'boshqaruv', label: 'Xodim qo‘shish va PIN berish', description: 'Xodimlar ro‘yxati, PIN, faollik' },
  { key: 'staff.roles', group: 'boshqaruv', label: 'Rol va huquqlarni o‘zgartirish', description: 'Rollar va shaxsiy istisnolarni sozlash', sensitive: true },
  { key: 'settings.edit', group: 'boshqaruv', label: 'Restoran sozlamalari', description: 'Chek, smena, zal va boshqa sozlamalar' },
  { key: 'devices.manage', group: 'boshqaruv', label: 'Qurilma ulash va uzish', description: 'Ulash kodi olish, qurilmani uzish', sensitive: true },
  { key: 'billing.view', group: 'boshqaruv', label: 'Obuna va to‘lovlar', description: 'Tarif, muddat va to‘lovlar tarixi' },
] as const satisfies readonly PermissionDef[];

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSIONS.map((p) => p.key);

const PERMISSION_MAP: ReadonlyMap<string, PermissionDef> = new Map(
  PERMISSIONS.map((p) => [p.key, p as PermissionDef] as const),
);

export function getPermission(key: string): PermissionDef | undefined {
  return PERMISSION_MAP.get(key);
}

/** Rollar sahifasida ko'rsatiladigan huquqlar (tayyor bo'lmaganlari yashirin) */
export function isPermissionVisible(def: PermissionDef): boolean {
  return !def.planned;
}

export function isPermissionKey(key: string): key is PermissionKey {
  return PERMISSION_MAP.has(key);
}

// ---------------------------------------------------------------------------
// Yakuniy huquqlarni hisoblash: rol + shaxsiy istisnolar + yoqilgan modullar.
// Serverdagi SQL funksiyasi aynan shu mantiqni takrorlaydi (testlar bilan).
// ---------------------------------------------------------------------------
export interface PermissionOverride {
  key: PermissionKey;
  /** true = qo'shiladi, false = olib tashlanadi */
  allow: boolean;
}

export function resolvePermissions(
  rolePermissions: readonly PermissionKey[],
  overrides: readonly PermissionOverride[],
  enabledModules: readonly string[],
): Set<PermissionKey> {
  const result = new Set<PermissionKey>(rolePermissions);
  for (const o of overrides) {
    if (o.allow) result.add(o.key);
    else result.delete(o.key);
  }
  const modules = new Set(enabledModules);
  for (const key of [...result]) {
    const def = PERMISSION_MAP.get(key);
    if (!def || def.planned || (def.module && !modules.has(def.module))) result.delete(key);
  }
  // Asosiy huquqi yo'q bo'lsa — amalda ishlamaydi (chegirma savdosiz va h.k.)
  for (const key of [...result]) {
    const req = PERMISSION_MAP.get(key)?.requires ?? [];
    if (req.some((r) => !result.has(r as PermissionKey))) result.delete(key);
  }
  return result;
}
